import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CMSImage, CMSImageType } from "../../../../../models/CMS/cmsImage";
import { SectionWrapper } from "../../../../UI/SectionWrapper/SectionWrapper";
import { store } from "../../../store";
import {
  ClearUploadedImageAction,
  getUploadedImagesSelector,
} from "../../../store/reducers/cmsReducer";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { FieldArray, FormikProps } from "formik";
import LanguageTabs from "../../../ui-components/pageLayout/LanguageTabs";
import LocalizedImageForm from "./LocalizedImageForm";

interface ILocalizedImagesProps {
  defaultExpanded?: boolean;
  assetId: string;
  setFieldValue: (field: string, value: any) => void;
}

interface FieldArrayRenderProps {
  form: FormikProps<{ images: CMSImage[] }>;
  push: (obj: CMSImage) => void;
  remove: (index: number) => void;
}

const LocalizedImagesAccordion: React.FC<ILocalizedImagesProps> = (props) => {
  const { defaultExpanded = false, assetId, setFieldValue } = props;

  const [langIndex, setLangIndex] = useState<number>(0);
  // can be fetched async per customer and stored in store
  const availableLanguages = ["en", "sv", "no"];
  const [file, setFile] = useState<File | undefined>(undefined);

  const newUploadedImages = useSelector(getUploadedImagesSelector);

  const newImage = {
    language: availableLanguages[langIndex],
    type: CMSImageType.Other,
    url: "",
    blobPath: "",
    width: 0,
    height: 0,
    source: "",
  };

  useEffect(() => {
    if (newUploadedImages.length > 0) {
      newUploadedImages.forEach((img) => {
        setFieldValue(`images[${img.index}].blobPath`, img.blobPath);
        setFieldValue(`images[${img.index}].url`, img.blobPath);
        setFieldValue(`images[${img.index}].source`, img.url);
      });
      store.dispatch(ClearUploadedImageAction());
    }
  }, [newUploadedImages]);

  const matchesCurrentLanguage = (lng: string) =>
    lng.toLowerCase() === availableLanguages[langIndex];

  return (
    <SectionWrapper>
      <Accordion defaultExpanded={defaultExpanded}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="accordion-summary-gray"
        >
          Localized images
        </AccordionSummary>
        <FieldArray
          name="images"
          render={({
            form: {
              values: { images },
            },
            push,
            remove,
          }: FieldArrayRenderProps) => (
            <AccordionDetails style={{ paddingTop: "0px" }}>
              <LanguageTabs
                languages={availableLanguages}
                setLanguageIndex={(index) => setLangIndex(index)}
              />
              {images.map(
                (image, index) =>
                  matchesCurrentLanguage(image.language) && (
                    <LocalizedImageForm
                      key={index}
                      index={index}
                      remove={remove}
                      file={file}
                      setFile={setFile}
                      assetId={assetId}
                      image={image}
                      setFieldValue={setFieldValue}
                    />
                  )
              )}
              <Button variant="contained" onClick={() => push(newImage)}>
                + Add image
              </Button>
            </AccordionDetails>
          )}
        />
      </Accordion>
    </SectionWrapper>
  );
};

export default LocalizedImagesAccordion;
